import React, { useState } from 'react';
import { useOrganization, ORGANIZATIONS } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { safeMap, ensureArray } from '../utils/safeArray';
import { ChevronDownIcon, LogOutIcon, BuildingIcon, SettingsIcon, UserIcon, KeyboardIcon, CheckIcon } from 'lucide-react';
import { SettingsModal } from './SettingsModal';
import { KeyboardShortcutsModal } from './KeyboardShortcutsModal';
export function TopBar() {
  const {
    currentOrganization,
    setCurrentOrganization,
    t
  } = useOrganization();
  const {
    role,
    setRole,
    logout
  } = useAuth();
  const [showOrgMenu, setShowOrgMenu] = useState(false);
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const isClientAdmin = role === 'client-admin';
  // Safe defaults for rendering
  const orgName = currentOrganization?.name || 'Loading...';
  const orgId = currentOrganization?.spacesOrgId || 'org-id';
  // Safe organizations array
  const safeOrganizations = ensureArray('TopBar.ORGANIZATIONS', ORGANIZATIONS);
  // Translation fallbacks
  const translations = {
    switchRole: t.switchRole || 'Switch Role',
    agilebrainAdmin: t.agilebrainAdmin || 'AgileBrain Admin',
    clientAdmin: t.clientAdmin || 'Client Admin',
    keyboardShortcuts: t.keyboardShortcuts || 'Keyboard Shortcuts',
    settings: t.settings || 'Settings',
    logout: t.logout || 'Logout'
  };
  return <>
      <div className="sticky top-0 z-40 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8 shadow-sm" data-id="element-503">
        <div className="flex items-center gap-4" data-id="element-504">
          {isClientAdmin ? <div className="flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg" data-id="element-505">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-506">
                <BuildingIcon className="w-4 h-4 text-white" data-id="element-507" />
              </div>
              <div className="text-left" data-id="element-508">
                <div className="text-sm font-semibold text-gray-900 leading-tight" data-id="element-509">
                  {orgName}
                </div>
                <div className="text-xs text-gray-500 font-mono leading-tight mt-0.5" data-id="element-510">
                  {orgId}
                </div>
              </div>
            </div> : <div className="relative" data-id="element-511">
              <button onClick={() => setShowOrgMenu(!showOrgMenu)} className="flex items-center gap-3 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-512">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-513">
                  <BuildingIcon className="w-4 h-4 text-white" data-id="element-514" />
                </div>
                <div className="text-left" data-id="element-515">
                  <div className="text-sm font-semibold text-gray-900 leading-tight" data-id="element-516">
                    {orgName}
                  </div>
                  <div className="text-xs text-gray-500 font-mono leading-tight mt-0.5" data-id="element-517">
                    {orgId}
                  </div>
                </div>
                <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" data-id="element-518" />
              </button>
              {showOrgMenu && <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowOrgMenu(false)} data-id="element-519" />
                  <div className="absolute top-full mt-2 left-0 w-80 bg-white border-2 border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden max-h-96 overflow-y-auto" data-id="element-520">
                    <div className="p-2" data-id="element-521">
                      {safeMap('TopBar.organizationMenu', safeOrganizations, org => {
                  const isSelected = currentOrganization?.id === org.id;
                  return <button key={org.id} onClick={() => {
                    setCurrentOrganization(org);
                    setShowOrgMenu(false);
                  }} className={`w-full px-4 py-3 text-left rounded-lg transition-all duration-200 flex items-center gap-3 ${isSelected ? 'bg-blue-50 border-2 border-blue-200' : 'hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-522">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-blue-500' : 'bg-gray-200'}`} data-id="element-523">
                                <BuildingIcon className={`w-4 h-4 ${isSelected ? 'text-white' : 'text-gray-600'}`} data-id="element-524" />
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-525">
                                <div className="font-semibold text-gray-900 text-sm truncate" data-id="element-526">
                                  {org.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5 font-mono truncate" data-id="element-527">
                                  {org.spacesOrgId}
                                </div>
                              </div>
                              {isSelected && <CheckIcon className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-528" />}
                            </button>;
                })}
                    </div>
                  </div>
                </>}
            </div>}
        </div>
        <div className="flex items-center gap-2" data-id="element-529">
          <div className="relative" data-id="element-530">
            <button onClick={() => setShowRoleMenu(!showRoleMenu)} className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" title={translations.switchRole} data-id="element-531">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-532">
                <UserIcon className="w-4 h-4 text-white" data-id="element-533" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-534">
                {role === 'agilebrain-admin' ? translations.agilebrainAdmin : translations.clientAdmin}
              </span>
              <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" data-id="element-535" />
            </button>
            {showRoleMenu && <>
                <div className="fixed inset-0 z-30" onClick={() => setShowRoleMenu(false)} data-id="element-536" />
                <div className="absolute top-full mt-2 right-0 w-64 bg-white border-2 border-gray-200 rounded-xl shadow-2xl z-50 overflow-hidden" data-id="element-537">
                  <div className="p-2" data-id="element-538">
                    <button onClick={() => {
                  setRole('agilebrain-admin');
                  setShowRoleMenu(false);
                }} className={`w-full px-4 py-3 text-left text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-between ${role === 'agilebrain-admin' ? 'bg-purple-50 text-purple-700 border-2 border-purple-200' : 'text-gray-700 hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-539">
                      <span data-id="element-540">{translations.agilebrainAdmin}</span>
                      {role === 'agilebrain-admin' && <CheckIcon className="w-5 h-5 text-purple-600 flex-shrink-0" data-id="element-541" />}
                    </button>
                    <button onClick={() => {
                  setRole('client-admin');
                  setShowRoleMenu(false);
                }} className={`w-full px-4 py-3 text-left text-sm font-medium rounded-lg transition-all duration-200 flex items-center justify-between ${role === 'client-admin' ? 'bg-purple-50 text-purple-700 border-2 border-purple-200' : 'text-gray-700 hover:bg-gray-50 border-2 border-transparent'}`} data-id="element-542">
                      <span data-id="element-543">{translations.clientAdmin}</span>
                      {role === 'client-admin' && <CheckIcon className="w-5 h-5 text-purple-600 flex-shrink-0" data-id="element-544" />}
                    </button>
                  </div>
                </div>
              </>}
          </div>
          <button onClick={() => setShowKeyboardShortcuts(true)} className="flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 border-2 border-transparent hover:border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" title={translations.keyboardShortcuts} data-id="element-545">
            <KeyboardIcon className="w-5 h-5" data-id="element-546" />
          </button>
          <button onClick={() => setShowSettings(true)} className="flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 border-2 border-transparent hover:border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" title={translations.settings} data-id="element-547">
            <SettingsIcon className="w-5 h-5" data-id="element-548" />
          </button>
          <div className="w-px h-8 bg-gray-200 mx-1" data-id="element-549" />
          <button onClick={logout} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 border-2 border-transparent hover:border-red-200 focus:outline-none focus:ring-2 focus:ring-red-500" data-id="element-550">
            <LogOutIcon className="w-4 h-4" data-id="element-551" />
            <span data-id="element-552">{translations.logout}</span>
          </button>
        </div>
      </div>
      <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} data-id="element-553" />
      <KeyboardShortcutsModal isOpen={showKeyboardShortcuts} onClose={() => setShowKeyboardShortcuts(false)} data-id="element-554" />
    </>;
}